// ===== 1. Imports =====
import React from "react";
import Modal from "../../components/Modal";
import { db } from "../../firebaseConfig";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { CheckCircle2, XCircle, Eye } from "lucide-react";
import { fmt, formatStatus } from "./utils/formatters";
import { canApprove, canUnapprove } from "./utils/rolePermissions";

// ===== 2. Componente =====
export default function POModal({
  projectCode,
  po,
  invoicesIndex,
  userRole,
  userName,
  userCanManageStatus,
  onClose,
  onEditInvoice,
}) {
  if (!po) return null;
  const approvals = po.approvals || {};

  // ===== 3. Aprobar / retirar =====
  const toggleApprove = async (approve) => {
    try {
      const now = new Date().toISOString();
      const updated = { ...approvals };

      if (userRole === "HOD") {
        updated.hod = approve
          ? { approved: true, name: userName, date: now }
          : { approved: false, name: "", date: "" };
      } else if (userRole === "PM") {
        if (updated.requiresHod && !updated.hod?.approved) {
          alert("Aún no ha aprobado el HOD.");
          return;
        }
        updated.pm = approve
          ? { approved: true, name: userName, date: now }
          : { approved: false, name: "", date: "" };
      } else if (userRole === "Controller") {
        if (!updated.pm?.approved) {
          alert("Aún no ha aprobado el PM.");
          return;
        }
        updated.controller = approve
          ? { approved: true, name: userName, date: now }
          : { approved: false, name: "", date: "" };
      }

      const fullyApproved =
        (!updated.requiresHod || updated.hod?.approved) &&
        updated.pm?.approved &&
        updated.controller?.approved;

      const newStatus = fullyApproved ? "Aprobada" : "Pendiente";

      await updateDoc(doc(db, "projects", projectCode, "pos", po.id), {
        approvals: updated,
        status: newStatus,
        modified: true,
        modifiedBy: userName,
        modifiedAt: serverTimestamp(),
        version: (po.version || 1) + 1,
      });
    } catch (err) {
      console.error("Error al aprobar/desaprobar:", err);
      alert("Error al actualizar la aprobación.");
    }
  };

  // ===== 4. Cambiar estado =====
  const handleChangeStatus = async (action) => {
    let newStatus = po.status;
    if (action === "anular") newStatus = "Anulada";
    if (action === "cerrar") newStatus = "Cerrada";
    if (action === "reabrir" || action === "desanular") newStatus = "Pendiente";

    await updateDoc(doc(db, "projects", projectCode, "pos", po.id), {
      status: newStatus,
      modified: true,
      modifiedBy: userName,
      modifiedAt: serverTimestamp(),
      version: (po.version || 1) + 1,
    });
  };

  // ===== 5. Facturas asociadas =====
  const invList = invoicesIndex?.[po.id] || [];
  const { bg, label } = formatStatus(po.status);

  // ===== 6. Cálculo financiero =====
  const summary = (po.items || []).reduce(
    (acc, i) => {
      const base = Number(i.base) || 0;
      const ivaVal = (base * (Number(i.iva) || 0)) / 100;
      const irpfVal = (base * (Number(i.irpf) || 0)) / 100;
      acc.base += base;
      acc.iva += ivaVal;
      acc.irpf += irpfVal;
      acc.total += base + ivaVal - irpfVal;
      return acc;
    },
    { base: 0, iva: 0, irpf: 0, total: 0 }
  );

  // ===== 7. Render =====
  return (
    <Modal
      title={`Detalle PO ${po.number}`}
      onClose={onClose}
      className="modal--large"
      style={{ maxWidth: "1000px" }}
    >
      <div className="stack">
        {/* --- Info general --- */}
        <div
          className="card"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            background: "#f9fafb",
            padding: 16,
          }}
        >
          <div>
            <div>
              <strong>Proveedor:</strong> {po.supplier}
            </div>
            <div>
              <strong>Departamento:</strong> {po.department}
            </div>
            <div>
              <strong>Creado por:</strong> {po.createdBy}
            </div>
          </div>
          <div>
            <div>
              <strong>Estado:</strong>{" "}
              <span
                style={{
                  background: bg,
                  color: "#fff",
                  padding: "3px 10px",
                  borderRadius: 8,
                }}
              >
                {label}
              </span>{" "}
              <span style={{ color: "var(--muted)", fontSize: 12 }}>
                v{po.version || 1}
              </span>
            </div>
            <div>
              <strong>Tipo:</strong> {po.type}
            </div>
            <div>
              <strong>Fecha creación:</strong>{" "}
              {po.createdAt?.toDate
                ? po.createdAt.toDate().toLocaleString()
                : "—"}
            </div>
          </div>
        </div>

        {/* --- Resumen financiero --- */}
        <div
          className="card"
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            background: "#f1f5f9",
            padding: "12px 16px",
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <div>
            Base imponible: <strong>{fmt(summary.base)}</strong>
          </div>
          <div>
            IVA: <strong>{fmt(summary.iva)}</strong>
          </div>
          <div>
            IRPF: <strong>{fmt(summary.irpf)}</strong>
          </div>
          <div>
            Total PO:{" "}
            <strong style={{ color: "var(--accent)", fontSize: 17 }}>
              {fmt(summary.total)}
            </strong>
          </div>
        </div>

        {/* --- Descripción --- */}
        {po.description && (
          <div>
            <strong>Descripción:</strong>
            <p style={{ whiteSpace: "pre-wrap" }}>{po.description}</p>
          </div>
        )}

        {/* --- Ítems --- */}
        <h3>Ítems</h3>
        <table className="users-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th>Base</th>
              <th>IVA</th>
              <th>IRPF</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {(po.items || []).map((i, idx) => {
              const base = Number(i.base) || 0;
              const iva = (base * (Number(i.iva) || 0)) / 100;
              const irpf = (base * (Number(i.irpf) || 0)) / 100;
              const total = base + iva - irpf;
              return (
                <tr key={idx}>
                  <td>{i.subaccount || "—"}</td>
                  <td>{i.description}</td>
                  <td style={{ textAlign: "right" }}>{fmt(base)}</td>
                  <td>{i.iva}%</td>
                  <td>{i.irpf}%</td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{fmt(total)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* --- Aprobaciones --- */}
        <h3>Aprobaciones</h3>
        <div className="card" style={{ padding: 16 }}>
          {[
            approvals.requiresHod && { key: "hod", label: "HOD" },
            { key: "pm", label: "PM" },
            { key: "controller", label: "Controller" },
          ]
            .filter(Boolean)
            .map(({ key, label }) => {
              const info = approvals[key] || {};
              const approved = !!info.approved;
              const isMyRow = userRole.toLowerCase() === label.toLowerCase();
              const canDo = canApprove(po, userRole, po.department);
              const canUndo = canUnapprove(po, userRole, po.department);

              const waitingPrevious =
                label === "PM" &&
                approvals.requiresHod &&
                !approvals.hod?.approved
                  ? "Esperando aprobación del HOD"
                  : label === "Controller" && !approvals.pm?.approved
                  ? "Esperando aprobación del PM"
                  : null;

              return (
                <div
                  key={key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr auto",
                    alignItems: "center",
                    borderBottom: "1px solid var(--ring)",
                    padding: "8px 0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 600,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: approved ? "var(--accent)" : "#e5e7eb",
                        color: approved ? "#fff" : "#6b7280",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                      }}
                    >
                      {label[0]}
                    </div>
                    {label}
                  </div>

                  <div
                    style={{
                      color: approved ? "var(--accent)" : "var(--muted)",
                      fontSize: 14,
                    }}
                  >
                    {approved
                      ? `Aprobado por ${info.name} el ${
                          info.date ? new Date(info.date).toLocaleString() : ""
                        }`
                      : waitingPrevious || "Pendiente"}
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    {isMyRow && !approved && canDo && !waitingPrevious && (
                      <button
                        className="button button--primary"
                        onClick={() => toggleApprove(true)}
                      >
                        Aprobar
                      </button>
                    )}
                    {isMyRow && approved && canUndo && (
                      <button
                        className="button"
                        onClick={() => toggleApprove(false)}
                      >
                        Retirar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

        {/* --- Estado administrativo --- */}
        {userCanManageStatus && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              justifyContent: "space-between",
              marginTop: 16,
            }}
          >
            <button
              className="button"
              onClick={() => handleChangeStatus("cerrar")}
            >
              <CheckCircle2 size={16} style={{ marginRight: 6 }} /> Cerrar PO
            </button>
            <button
              className="button"
              onClick={() => handleChangeStatus("anular")}
            >
              <XCircle size={16} style={{ marginRight: 6 }} /> Anular PO
            </button>
            {po.status === "Anulada" && (
              <button
                className="button button--primary"
                onClick={() => handleChangeStatus("desanular")}
              >
                Desanular PO
              </button>
            )}
            {po.status === "Cerrada" && (
              <button
                className="button button--primary"
                onClick={() => handleChangeStatus("reabrir")}
              >
                Reabrir PO
              </button>
            )}
          </div>
        )}

        {/* --- Facturas asociadas --- */}
        <h3>Facturas asociadas</h3>
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Proveedor</th>
              <th>Base</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invList.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ textAlign: "center", color: "#9ca3af" }}
                >
                  No hay facturas asociadas a esta PO.
                </td>
              </tr>
            ) : (
              invList.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.id}</td>
                  <td>{inv.supplier || po.supplier}</td>
                  <td style={{ textAlign: "right" }}>{fmt(inv.baseAmount)}</td>
                  <td style={{ textAlign: "right" }}>{fmt(inv.totalAmount)}</td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="button button--ghost"
                      onClick={() => onEditInvoice({ ...inv, _poId: po.id })}
                      title="Ver factura"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
